import type { Voice } from "@/app/_data/voices";
import type { ICON } from "@ui/tokens/icon-types";

import { simpleStringHash } from "@utilities/hash";

import type { DemoPrompts } from "../demos/TTS/config";

export interface IPrompt {
  icon: ICON;
  text: string;
  title: string;
}

export enum PromptFeature {
  AUDIO_TAGS = "audioTags",
  DIALOGUE = "dialogue",
}

export type Features = Partial<Record<PromptFeature, boolean>>;

export interface SimplePromptRequirement {
  /** Features to distribute among. If empty, accepts all prompts */
  distributeFeatures?: PromptFeature[];
  /** Whether to include prompts without any specified features */
  includeUntagged?: boolean;
  /** Maximum number of prompts to return */
  maxPrompts: number;
}

export interface DialogueItem {
  speaker: string;
  text: string;
}

/** Creates a simple unique ID for a prompt. Not to be confused with the cache key, which is generated based on other parameters as well (voice, speed, model, etc.) */
export function generatePromptId(prompt: IUseCasePrompt): string {
  return simpleStringHash(`${prompt.useCase}-${prompt.title}-${prompt.description}`).toString();
}

export interface IUseCasePrompt extends Omit<IPrompt, "icon"> {
  description: string;
  dialogue?: DialogueItem[];
  features?: Features;
  preferredVoices?: Voice[];
  useCase: UseCase;
}

export interface IUseCasePromptWithId extends IUseCasePrompt {
  id: string;
}

export enum UseCase {
  CUSTOM_PROMPT = "customPrompt",
  CUSTOMER_SUPPORT = "customerSupport",
  DIFFERENT_LANGUAGE = "differentLanguage",
  DIRECT_A_MOVIE = "directAMovie",
  JOKE = "joke",
  MEDITATION = "meditation",
  NARRATION = "narration",
  VIDEO_GAME_CHARACTER = "videoGameCharacter",
  VIDEO_VOICEOVER = "voiceover",
}

export function getUniqueUseCasePrompts(
  selectedLanguageUseCasePrompts?: IUseCasePromptWithId[],
): Array<{ prompt: IUseCasePromptWithId; useCase: UseCase }> {
  const useCasesInPriorityOrder = [
    UseCase.NARRATION,
    UseCase.JOKE,
    UseCase.DIFFERENT_LANGUAGE,
    UseCase.CUSTOMER_SUPPORT,
    UseCase.VIDEO_VOICEOVER,
    UseCase.MEDITATION,
    UseCase.DIRECT_A_MOVIE,
    UseCase.VIDEO_GAME_CHARACTER,
  ];
  return useCasesInPriorityOrder
    .map(useCase => {
      const prompt = selectedLanguageUseCasePrompts?.find(p => p.useCase === useCase);
      if (!prompt) return null;
      return {
        prompt,
        useCase,
      };
    })
    .filter((item): item is { prompt: IUseCasePromptWithId; useCase: UseCase } => item !== null);
}

function hasFeature(prompt: IUseCasePrompt, feature: PromptFeature): boolean {
  return prompt.features?.[feature] === true;
}

function hasDialogue(prompt: IUseCasePrompt): boolean {
  return Boolean(prompt.dialogue && prompt.dialogue.length > 0);
}

function getUniqueSpeakers(prompt: IUseCasePrompt): string[] {
  if (!hasDialogue(prompt)) return [];
  const speakers = new Set<string>();
  prompt.dialogue?.forEach(item => speakers.add(item.speaker));
  return Array.from(speakers);
}

function getVoicesForPrompt(
  prompt: IUseCasePromptWithId,
  voices: Voice[],
  startIndex: number,
): { nextIndex: number; voices: Voice[] } {
  const voicesNeeded = hasDialogue(prompt) ? getUniqueSpeakers(prompt).length : 1;

  // Try to use preferred voices first if available
  if (prompt.preferredVoices && prompt.preferredVoices.length > 0) {
    // Filter preferred voices to only include ones that are available in the voices array
    const availablePreferredVoices = prompt.preferredVoices.filter(preferredVoice =>
      voices.some(voice => voice.voiceId === preferredVoice.voiceId),
    );

    if (availablePreferredVoices.length >= voicesNeeded) {
      // We have enough preferred voices available
      return {
        voices: availablePreferredVoices.slice(0, voicesNeeded),
        nextIndex: startIndex, // Don't advance the index since we didn't use the cycling voices
      };
    } else if (availablePreferredVoices.length > 0) {
      // We have some preferred voices but not enough, use what we have plus fill from cycling
      const remainingNeeded = voicesNeeded - availablePreferredVoices.length;
      const assignedVoices = [...availablePreferredVoices];

      // Fill remaining slots with cycling voices, avoiding duplicates
      let currentIndex = startIndex;
      for (let i = 0; i < remainingNeeded; i++) {
        let voice = voices[currentIndex % voices.length];
        // Skip if this voice is already assigned from preferred voices
        while (assignedVoices.some(av => av.voiceId === voice.voiceId)) {
          currentIndex = (currentIndex + 1) % voices.length;
          voice = voices[currentIndex % voices.length];
        }
        assignedVoices.push(voice);
        currentIndex = (currentIndex + 1) % voices.length;
      }

      return {
        voices: assignedVoices,
        nextIndex: currentIndex,
      };
    }
  }

  // Fall back to original cycling logic
  if (hasDialogue(prompt)) {
    const assignedVoices: Voice[] = [];

    for (let i = 0; i < voicesNeeded; i++) {
      const voiceIndex = (startIndex + i) % voices.length;
      assignedVoices.push(voices[voiceIndex]);
    }

    return {
      voices: assignedVoices,
      nextIndex: (startIndex + voicesNeeded) % voices.length,
    };
  } else {
    // Single voice for non-dialogue prompts
    return {
      voices: [voices[startIndex % voices.length]],
      nextIndex: (startIndex + 1) % voices.length,
    };
  }
}

function canAssignVoicesToPrompt(prompt: IUseCasePrompt, availableVoiceCount: number): boolean {
  if (!hasDialogue(prompt)) return true; // Non-dialogue prompts only need 1 voice
  const speakerCount = getUniqueSpeakers(prompt).length;
  return speakerCount <= availableVoiceCount;
}

function getPromptFeatures(prompt: IUseCasePrompt): PromptFeature[] {
  const features: PromptFeature[] = [];

  if (hasDialogue(prompt)) {
    features.push(PromptFeature.DIALOGUE);
  }

  if (hasFeature(prompt, PromptFeature.AUDIO_TAGS)) {
    features.push(PromptFeature.AUDIO_TAGS);
  }

  return features;
}

function isPromptAcceptable(prompt: IUseCasePrompt, requirement: SimplePromptRequirement): boolean {
  const promptFeatures = getPromptFeatures(prompt);

  // Special case: if no features specified but includeUntagged is true, only accept untagged prompts
  if (
    (!requirement.distributeFeatures || requirement.distributeFeatures.length === 0) &&
    requirement.includeUntagged === true
  ) {
    return promptFeatures.length === 0; // Only untagged prompts
  }

  // If no features specified and includeUntagged is not explicitly true, accept all prompts
  if (!requirement.distributeFeatures || requirement.distributeFeatures.length === 0) {
    return true;
  }

  // Check if prompt has any of the desired features
  const hasDesiredFeature = requirement.distributeFeatures.some(feature => promptFeatures.includes(feature));

  // If prompt has no features, only accept if includeUntagged is true
  if (promptFeatures.length === 0) {
    return requirement.includeUntagged === true;
  }

  return hasDesiredFeature;
}

function distributePromptsIntelligently(
  prompts: IUseCasePromptWithId[],
  requirement: SimplePromptRequirement,
): IUseCasePromptWithId[] {
  // Special case: only untagged prompts requested
  if (
    (!requirement.distributeFeatures || requirement.distributeFeatures.length === 0) &&
    requirement.includeUntagged === true
  ) {
    const untaggedPrompts = prompts.filter(prompt => getPromptFeatures(prompt).length === 0);
    return untaggedPrompts.slice(0, requirement.maxPrompts);
  }

  if (!requirement.distributeFeatures || requirement.distributeFeatures.length === 0) {
    // No specific distribution needed, just return up to maxPrompts
    return prompts.slice(0, requirement.maxPrompts);
  }

  // Group prompts by their features
  const featureGroups: Record<string, IUseCasePromptWithId[]> = {};
  const untaggedPrompts: IUseCasePromptWithId[] = [];

  prompts.forEach(prompt => {
    const promptFeatures = getPromptFeatures(prompt);

    if (promptFeatures.length === 0) {
      untaggedPrompts.push(prompt);
    } else {
      // Add prompt to each feature group it belongs to
      promptFeatures.forEach(feature => {
        if (requirement.distributeFeatures!.includes(feature)) {
          if (!featureGroups[feature]) {
            featureGroups[feature] = [];
          }
          featureGroups[feature].push(prompt);
        }
      });
    }
  });

  // Calculate how many groups we're distributing among
  const groups: { key: string; prompts: IUseCasePromptWithId[] }[] = [];

  requirement.distributeFeatures.forEach(feature => {
    if (featureGroups[feature] && featureGroups[feature].length > 0) {
      groups.push({ key: feature, prompts: featureGroups[feature] });
    }
  });

  if (requirement.includeUntagged && untaggedPrompts.length > 0) {
    groups.push({ key: "untagged", prompts: untaggedPrompts });
  }

  if (groups.length === 0) {
    return [];
  }

  // Distribute prompts equally among groups
  const selectedPrompts: IUseCasePromptWithId[] = [];
  const usedPrompts = new Set<IUseCasePromptWithId>();

  // Round-robin selection to ensure even distribution
  let currentIndex = 0;
  let consecutiveEmptyRounds = 0;

  while (selectedPrompts.length < requirement.maxPrompts && groups.some(g => g.prompts.length > 0)) {
    const group = groups[currentIndex % groups.length];

    // Find next unused prompt in this group
    const availablePrompt = group.prompts.find(p => !usedPrompts.has(p));
    if (availablePrompt) {
      selectedPrompts.push(availablePrompt);
      usedPrompts.add(availablePrompt);

      // Remove from group to avoid selecting again
      group.prompts = group.prompts.filter(p => p !== availablePrompt);
      consecutiveEmptyRounds = 0; // Reset counter
    } else {
      consecutiveEmptyRounds++;
    }

    // Safety check: if we've gone through all groups without finding any prompts, break
    if (consecutiveEmptyRounds >= groups.length) {
      break;
    }

    currentIndex++;
  }

  return selectedPrompts;
}

export function getVoiceToUseCasePromptAssignment(
  voices: Voice[],
  demoPrompts?: DemoPrompts,
  requirement?: SimplePromptRequirement,
): { assignedVoices: Voice[]; prompt: IUseCasePromptWithId }[] {
  if (!voices.length) return [];

  const finalAssignments: { assignedVoices: Voice[]; prompt: IUseCasePromptWithId }[] = [];
  const availablePrompts = (demoPrompts?.useCasePrompts || []).filter(prompt =>
    canAssignVoicesToPrompt(prompt, voices.length),
  );
  let voiceIndex = 0;

  // First, handle samplePrompts if they exist
  if (demoPrompts?.samplePrompts && demoPrompts.samplePrompts.length > 0) {
    // Assign voices to samplePrompts using cycling
    demoPrompts.samplePrompts.forEach(samplePrompt => {
      const prompt: IUseCasePromptWithId = {
        title: samplePrompt.title,
        text: samplePrompt.prompt,
        description: samplePrompt.title,
        useCase: UseCase.CUSTOM_PROMPT,
        id: generatePromptId({
          title: samplePrompt.title,
          text: samplePrompt.prompt,
          description: samplePrompt.title,
          useCase: UseCase.CUSTOM_PROMPT,
        }),
      };

      // Only assign if we can provide enough voices
      if (canAssignVoicesToPrompt(prompt, voices.length)) {
        const { voices: assignedVoices, nextIndex } = getVoicesForPrompt(prompt, voices, voiceIndex);
        finalAssignments.push({ prompt, assignedVoices });
        voiceIndex = nextIndex;
      }
    });
  }

  // Handle intelligent prompt selection if requirement is specified
  if (requirement) {
    // Filter prompts that match the requirement
    const acceptablePrompts = availablePrompts.filter(prompt => isPromptAcceptable(prompt, requirement));

    // Intelligently distribute the prompts
    const selectedPrompts = distributePromptsIntelligently(acceptablePrompts, requirement);

    // Assign voices to selected prompts
    selectedPrompts.forEach(prompt => {
      const { voices: assignedVoices, nextIndex } = getVoicesForPrompt(prompt, voices, voiceIndex);
      finalAssignments.push({ prompt, assignedVoices });
      voiceIndex = nextIndex;
    });
  } else {
    // No specific requirement, use the old unique use case logic
    const uniquePrompts = getUniqueUseCasePrompts(availablePrompts);

    uniquePrompts.forEach(({ prompt }) => {
      const { voices: assignedVoices, nextIndex } = getVoicesForPrompt(prompt, voices, voiceIndex);
      finalAssignments.push({ prompt, assignedVoices });
      voiceIndex = nextIndex;
    });
  }

  return finalAssignments;
}
